import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text, TextLink } from "@/components/text";
import { Button } from "@/components/button";
import {
  ArrowLeftIcon,
  TrashIcon,
  UserGroupIcon,
  PlusIcon,
  UserMinusIcon,
  PencilIcon,
  EllipsisHorizontalIcon,
  ChartBarIcon,
  PrinterIcon,
} from "@heroicons/react/20/solid";
import {
  useQueryGetExamAssignment,
  useUpdateExamAssignment,
  useDeleteExamAssignment,
  addParticipants,
  removeParticipant,
} from "@/apis/exam_assignments";
import { useQueryGetExamSet } from "@/apis/exam_sets/getExamSet";
import { useQueryGetUserList } from "@/apis/users/getUserList";
import { Badge } from "@/components/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { Dialog } from "@/components/dialog";
import { Checkbox } from "@/components/checkbox";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "@/components/dropdown";
import StudentSelectModal from "./StudentSelectModal";
import { useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/switch";
import { useQueryGetExamAssignmentMonitor } from "@/apis/exam_assignments/getExamAssignmentMonitor";
import { ExamAssignmentMonitorTable } from "./components/ExamAssignmentMonitorTable";
import {
  hasAssignedSubject,
  isSubjectAvailableInExamSet,
} from "@/apis/exam_assignments/utils";
import { useMutationUpdateSubject } from "@/apis/exam_progress/updateSubject";
import type { SubjectType } from "@/apis/exam_progress/types";
import { QueryKeys } from "@/lib/query-keys";
import { getPaperTypeLabel } from "@/constants/exam";
import { getSectionLabel } from "@/utils/exam";
import { message } from "antd";

const ExamAssignmentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [actionError, setActionError] = useState<string | null>(null);
  const [isStudentManageModalOpen, setIsStudentManageModalOpen] =
    useState(false);
  const queryClient = useQueryClient();
  const { data: examAssignmentMonitor } = useQueryGetExamAssignmentMonitor(
    id || ""
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Invalid exam assignment ID.</Text>
          <Button
            onClick={() => navigate("/exam_assignments")}
            className="mt-4"
          >
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  // 시험 할당 정보 조회
  const {
    data: examAssignment,
    isLoading,
    error,
    refetch,
  } = useQueryGetExamAssignment(id);

  // 관련 시험 세트 정보 조회
  const { data: examSet } = useQueryGetExamSet(
    examAssignment?.exam_set_id || "",
    {
      enabled: !!examAssignment?.exam_set_id,
    }
  );

  // 사용자 목록 조회 (할당된 학생 및 할당자 정보용)
  const { data: usersData } = useQueryGetUserList({
    limit: 100,
    role: "student",
  });

  // 상태 업데이트 뮤테이션
  const { mutate: updateAssignment, isPending: isUpdating } =
    useUpdateExamAssignment();

  // 삭제 뮤테이션
  const { mutate: deleteExamAssignment, isPending: isDeleting } =
    useDeleteExamAssignment();

  // 과목 토글 뮤테이션
  const { mutate: updateSubject, isPending: isUpdatingSubject } =
    useMutationUpdateSubject();

  // 목록으로 돌아가기
  const handleBack = () => {
    window.history.back();
  };

  // 삭제 처리
  const handleDelete = () => {
    if (isDeleting) return;

    if (
      window.confirm("Are you sure you want to delete this exam assignment?")
    ) {
      setActionError(null);

      deleteExamAssignment(id, {
        onSuccess: () => {
          navigate("/exam_assignments");
        },
        onError: (error: any) => {
          setActionError(error?.message || "Failed to delete exam assignment");
        },
      });
    }
  };

  // 학생 삭제
  const handleRemoveStudent = async (studentId: string) => {
    if (isUpdating || !examAssignment) return;

    // 확인 메시지
    const confirmMessage = `⚠️ WARNING: PERMANENT DELETION ⚠️

Are you absolutely sure you want to remove this student from the exam assignment?

This action will:
• Permanently delete all their exam progress data
• Remove all their answers and scores
• Cannot be undone or recovered`;

    const confirmed = window.confirm(confirmMessage);

    if (!confirmed) {
      return; // 사용자가 취소를 선택한 경우
    }

    setActionError(null);

    try {
      await removeParticipant(id, studentId);

      // 성공 시 데이터 새로고침
      queryClient.invalidateQueries({
        queryKey: ["examAssignment", id],
      });
      queryClient.invalidateQueries({
        queryKey: QueryKeys.examAssignment.monitor(id),
      });

      // 성공 메시지 표시
      message.success("Student removed successfully");
    } catch (error: any) {
      setActionError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to remove student from exam assignment"
      );
    }
  };

  // 과목 토글 처리
  const handleSubjectToggle = (
    userId: string,
    subject: SubjectType,
    enabled: boolean
  ) => {
    if (isUpdatingSubject || !examAssignmentMonitor) return;

    // examAssignmentMonitor에서 해당 유저의 progress ID 찾기
    const userProgress = examAssignmentMonitor.progress_list.find(
      (item) => item.progress.user_id === userId
    );

    if (!userProgress) {
      setActionError("User progress not found");
      return;
    }

    setActionError(null);

    updateSubject(
      {
        progressId: userProgress.progress.id,
        request: {
          subject,
          enabled,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: QueryKeys.examAssignment.detail(id),
          });
          // Monitor 데이터도 새로고침
          queryClient.invalidateQueries({
            queryKey: QueryKeys.examAssignment.monitor(id),
          });
        },
        onError: (error: any) => {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to update subject";
          setActionError(errorMessage);
        },
      }
    );
  };

  // 사용자명 가져오기
  const getUserName = (userId: string) => {
    const user = usersData?.items.find((user) => user.id === userId);
    return user ? user.name : userId;
  };

  // 날짜 포맷 함수
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 학생 관리 모달 토글
  const toggleStudentManageModal = () => {
    setIsStudentManageModalOpen(!isStudentManageModalOpen);
  };

  // 학생 중 한 명이라도 모듈을 완료했는지 확인하는 함수
  const hasAnyCompletedModule = () => {
    if (!examAssignmentMonitor?.progress_list) return false;

    return examAssignmentMonitor.progress_list.some((progressItem) =>
      progressItem.progress.assigned_papers.some((paper) =>
        paper.module_progress.some((module) => module.status === "completed")
      )
    );
  };

  // 로딩 중
  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading exam assignment information...</Text>
        </div>
      </PageContainer>
    );
  }

  // 오류 발생
  if (error || !examAssignment) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>
            Failed to load exam assignment information. Please try again.
          </Text>
          <Button onClick={handleBack} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {actionError && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            {actionError}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button plain onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </Button>

            <div>
              <Heading>Exam Assignment Details</Heading>
              <Text className="text-zinc-500">
                View detailed information about this exam assignment.
              </Text>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() =>
                navigate(`/exam_assignments/${id}/results/analysis`)
              }
              className={
                hasAnyCompletedModule()
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-gray-400 text-gray-600 cursor-not-allowed"
              }
              disabled={!hasAnyCompletedModule()}
            >
              <ChartBarIcon className="h-4 w-4 mr-1" />
              Analysis Report
            </Button>

            <Dropdown>
              <DropdownButton outline>
                <EllipsisHorizontalIcon className="h-4 w-4" />
              </DropdownButton>
              <DropdownMenu>
                <DropdownItem
                  onClick={() => navigate(`/exam_assignments/${id}/edit`)}
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </DropdownItem>
                <DropdownItem
                  onClick={() => navigate(`/exam_assignments/${id}/print-questions`)}
                >
                  <PrinterIcon className="h-4 w-4 mr-2" />
                  Print Questions
                </DropdownItem>
                <DropdownItem
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="text-red-600"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  {isDeleting ? "Deleting..." : "Delete"}
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        {/* 시험 할당 상세 정보 */}
        <div className="grid grid-cols-1 gap-6">
          {/* 기본 정보 */}
          <div className="p-6 bg-white rounded-md border border-gray-200">
            <h2 className="text-lg font-medium mb-4">Assignment Information</h2>
            <dl className="grid grid-cols-2 gap-3">
              <dt className="text-sm font-medium text-gray-500">Exam Set</dt>
              <dd className="text-sm text-gray-900">{examSet?.title || "-"}</dd>

              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="text-sm text-gray-900">
                {formatDate(examAssignment.created_at)}
              </dd>

              {examAssignment.description && (
                <>
                  <dt className="text-sm font-medium text-gray-500 col-span-2">
                    Description
                  </dt>
                  <dd className="text-sm text-gray-900 col-span-2 bg-gray-50 p-2 rounded">
                    {examAssignment.description}
                  </dd>
                </>
              )}
            </dl>
          </div>

          {/* 시험지별 카드 */}
          {examSet && examSet.items && Array.isArray(examSet.items) && (
            <div className="space-y-4">
              <h2 className="text-lg font-medium">Exam Papers</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...examSet.items]
                  .sort((a, b) => a.order - b.order)
                  .map((item, idx) => {
                    const paper = item.exam_paper;
                    if (!paper) return null;

                    return (
                      <div
                        key={paper.id}
                        className="p-6 bg-white rounded-md border border-gray-200"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">
                              {getPaperTypeLabel(paper.subject)}
                            </h3>
                            <div className="text-sm text-gray-500">
                              ({paper.title})
                            </div>
                          </div>
                          <Button
                            color="white"
                            onClick={() => navigate(`/exam_papers/${paper.id}`)}
                          >
                            View Details
                          </Button>
                        </div>

                        <div className="space-y-3">
                          {paper.sections && paper.sections.length > 0 ? (
                            <div className="space-y-1">
                              {paper.sections.map((section, sectionIndex) => {
                                const sectionNum = sectionIndex + 1;
                                const questionCount = Array.isArray(
                                  section.question_ids
                                )
                                  ? section.question_ids.length
                                  : 0;
                                const duration = section.duration || 0;

                                return (
                                  <div
                                    key={sectionIndex}
                                    className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm"
                                  >
                                    <span className="font-medium">
                                      {getSectionLabel(examSet?.exam_type)}{" "}
                                      {sectionNum}
                                    </span>
                                    <span className="text-gray-600">
                                      {questionCount} questions • {duration} min
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400">
                              No sections configured
                            </div>
                          )}

                          {/* Score Table Warning for ACT papers */}
                          {examSet?.exam_type === "ACT" && !paper.score_conversion_table_id && (
                            <Text className="text-xs text-red-600">
                              Score table missing. {" "}
                              <TextLink href={`/score_conversion_tables/create?exam_paper_id=${paper.id}`}>
                                Create score table
                              </TextLink>
                            </Text>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* 모니터링 섹션 */}
        <ExamAssignmentMonitorTable
          progressList={examAssignmentMonitor?.progress_list}
          examSet={examSet}
          examAssignmentMonitor={examAssignmentMonitor}
          onSubjectToggle={handleSubjectToggle}
          onRemoveStudent={handleRemoveStudent}
          onToggleStudentManageModal={toggleStudentManageModal}
          isUpdatingSubject={isUpdatingSubject}
          isUpdating={isUpdating}
        />
      </div>

      {/* 학생 관리 모달 */}
      <StudentSelectModal
        open={isStudentManageModalOpen}
        onClose={toggleStudentManageModal}
        selectedUserIds={
          examAssignment?.target_users?.map((user) => user?.user_id) || []
        }
        onSelect={async (newStudentIds) => {
          if (!newStudentIds || newStudentIds.length === 0) {
            toggleStudentManageModal();
            return;
          }

          setActionError(null);

          try {
            await addParticipants(id, { user_ids: newStudentIds });

            // 성공 시 데이터 새로고침
            queryClient.invalidateQueries({
              queryKey: ["examAssignment", id],
            });
            queryClient.invalidateQueries({
              queryKey: QueryKeys.examAssignment.monitor(id),
            });

            toggleStudentManageModal();
          } catch (error: any) {
            setActionError(
              error?.response?.data?.message ||
                error?.message ||
                "Failed to add students to exam assignment"
            );
          }
        }}
      />
    </PageContainer>
  );
};

export default ExamAssignmentDetailPage;
